import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  AssistantType,
  FlowData,
  FlowIssue,
  FlowMode,
  SavingState,
  SettingParamTypes,
  Action,
  VariableTypes,
  WorkflowNodeSetting,
  WorkflowNodeTypes,
} from "@/types/node";
import {debounce} from "lodash";
import {Node, useReactFlow} from "@xyflow/react";
import {LATEST_FLOW_DATA_VERSION} from "@/lib/flowMigrations";
import {
  extractHashtagSections,
  generatePrompt,
  getAllActionIds,
  isEmptySettingValue,
  extractBracketedSections,
} from "@/utils/index";
import {editFlow, createFlow} from "@/api/flow";
import {findPromptValues} from "@/utils/index";
import {FlowBuilderSettings} from "@/config/flowBuilderSettings";
import {getVariableParams} from "@/components/settings/SettingPrompt";

const DEBOUNCE_TIME = 700;

interface SaveContextType {
  savingState: SavingState;
  handleSave: (afterSave?: () => void, forceSave?: boolean) => void;
  setSavingState: (state: SavingState) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (state: boolean) => void;
  flowIssues: FlowIssue[];
  setFlowIssues: (issues: FlowIssue[]) => void;
  flowMode: FlowMode;
  setFlowMode: (mode: FlowMode) => void;
  flowData: FlowData;
  setFlowData: (data: FlowData) => void;
}

const SaveContext = createContext<SaveContextType | undefined>(undefined);

export const findFlowDataIssues = (nodes: Node[]): FlowIssue[] => {
  return nodes.flatMap(node => {
    const settings = node.data?.settings as WorkflowNodeSetting[];
    const issues: FlowIssue[] =
      settings?.flatMap(setting =>
        (setting.params || []).flatMap(param => {
          const paramIssues: FlowIssue[] = [];

          if (param.required && isEmptySettingValue(param.value)) {
            paramIssues.push({
              type: "warning",
              message:
                param.requiredLabel ||
                `Missing required value for ${param.label}`,
              nodeId: node.id,
              nodeType: node.data?.type as WorkflowNodeTypes,
              key: param.key,
              nodeTitle: node.data?.settingsTitle as string,
            });
          }

          if (param.type === SettingParamTypes.ACTIONS) {
            const actions = (param.value as Action[]) || [];
            const actionIssues = actions
              .filter(action => action.missing === true)
              .map(action => {
                const meta = action as {
                  name?: string;
                  action_id?: string;
                };
                return {
                  type: "warning",
                  message: `Missing action ${meta.name || meta.action_id || ""}`,
                  nodeId: node.id,
                  nodeType: node.data?.type as WorkflowNodeTypes,
                  key: param.key,
                  nodeTitle: node.data?.settingsTitle as string,
                } as FlowIssue;
              });
            paramIssues.push(...actionIssues);
          }

          return paramIssues;
        }),
      ) || [];

    return issues;
  });
};

const processNodes = (nodes: Node[]) => {
  return nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      settings:
        (node.data?.settings as WorkflowNodeSetting[])?.reduce(
          (acc, setting) => {
            setting.params?.forEach(param => {
              acc[param.key] = param.value;
            });
            return acc;
          },
          {} as Record<string, any>,
        ) || {},
    },
  }));
};

export const SaveProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [flowMode, setFlowMode] = useState<FlowMode>(FlowMode.TEST);
  const [flowData, setFlowData] = useState<FlowData>({
    live: {version: 0, nodes: [], edges: []},
    test: {version: 0, nodes: [], edges: []},
  });

  const [savingState, setSavingState] = useState<SavingState>(
    SavingState.LOADING,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const {getNodes, getEdges} = useReactFlow();
  const [flowIssues, setFlowIssues] = useState<FlowIssue[]>([]);
  const flowDataRef = useRef(flowData);

  const debouncedSave = useMemo(
    () =>
      debounce((afterSave?: () => void) => {
        const nodes = getNodes();
        const edges = getEdges();
        const modelID = FlowBuilderSettings.modelId;

        setSavingState(SavingState.SAVING);

        const processedTestNodes = processNodes(nodes);
        const processedLiveNodes = processNodes(flowDataRef.current.live.nodes);

        const processedTestFlowData = {
          version: LATEST_FLOW_DATA_VERSION,
          nodes: processedTestNodes,
          edges: edges,
        };

        const processedLiveFlowData = {
          version: LATEST_FLOW_DATA_VERSION,
          nodes: processedLiveNodes,
          edges: flowDataRef.current.live.edges,
        };

        const promptValues = findPromptValues(nodes);
        const variablesData = getVariableParams(AssistantType.OUTBOUND);
        const customVariables = extractBracketedSections(promptValues, [
          variablesData[VariableTypes.VARIABLES].open,
          variablesData[VariableTypes.VARIABLES].close,
        ]);

        // Extract action results variables and remove the action prefix
        const actionResultsVariables = extractBracketedSections(promptValues, [
          variablesData[VariableTypes.RESULTS].open,
          variablesData[VariableTypes.RESULTS].close,
        ]).map((variable: string) =>
          variable.replace(
            new RegExp(`^${variablesData[VariableTypes.RESULTS].open}[^:]+:`),
            variablesData[VariableTypes.RESULTS].open,
          ),
        );

        const customActions = extractHashtagSections(promptValues);

        const startNode = processedLiveNodes.find(
          node => node.type === WorkflowNodeTypes.START,
        );
        const greeting = startNode?.data.settings?.scripted_greeting || "";

        const payload = {
          type: "string",
          stage_name: "string",
          stage_description: "string",
          stage_condition: "string",
          instruction: "string",
          order: 1,
          json: JSON.stringify(processedLiveFlowData),
          json_test: JSON.stringify(processedTestFlowData),
          prompt: generatePrompt(
            flowDataRef.current.live.nodes,
            flowDataRef.current.live.edges,
          ),
          actionsList: getAllActionIds(nodes),
          customActions,
          customVariables,
          actionResultsVariables,
          newVariables: [],
          scripted_greeting:
            greeting.length > 0 ? greeting : "Hello! How can I help you?",
          node_id: modelID,
          model_id: modelID,
        };

        const action =
          flowDataRef.current.live.version > 0 ? editFlow : createFlow;

        if (
          processedTestFlowData.nodes.length === 0 ||
          processedTestFlowData.edges.length === 0 ||
          processedLiveFlowData.nodes.length === 0 ||
          processedLiveFlowData.edges.length === 0
        ) {
          console.error("Empty nodes or edges detected");
          return;
        }

        console.log("processedLiveFlowData", processedLiveFlowData);
        console.log("processedTestFlowData", processedTestFlowData);

        action(payload)
          .then(data => {
            console.log(`${modelID ? "Node updated" : "Node created"}:`, data);

            setHasUnsavedChanges(false);
            setSavingState(SavingState.SAVED);
          })
          .catch(error => {
            console.error(error);
            setSavingState(SavingState.ERROR);
          })
          .finally(() => {
            afterSave && afterSave();
          });
      }, DEBOUNCE_TIME),
    [getNodes, getEdges, setHasUnsavedChanges, setSavingState],
  );

  const handleSave = useCallback(
    (afterSave?: () => void) => {
      if (flowMode === FlowMode.TEST && !FlowBuilderSettings.demoMode) {
        debouncedSave(afterSave);
      }
    },
    [flowMode, flowData],
  );

  useEffect(() => {
    flowDataRef.current = flowData;
  }, [flowData]);

  return (
    <SaveContext.Provider
      value={{
        savingState,
        handleSave,
        setSavingState,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        flowIssues,
        setFlowIssues,
        flowMode,
        setFlowMode,
        flowData,
        setFlowData,
      }}
    >
      {children}
    </SaveContext.Provider>
  );
};

export const useSave = () => {
  const context = useContext(SaveContext);
  if (!context) {
    throw new Error("useSave must be used within a SaveProvider");
  }
  return context;
};
